import { DefaultValues, FieldValues, useForm, UseFormReturn } from 'react-hook-form';
import { ZodType, z } from 'zod';
import zodToJsonSchema from 'zod-to-json-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';

export interface UseFormProps<T> {
  formSchema: ZodType<object, z.ZodTypeDef, object>;
  formDefaultValues: T;
  schemaTitle: string;
  mode?: 'onBlur' | 'onChange' | 'onSubmit' | 'onTouched' | 'all';
  values?: T;
  reValidateMode?: 'onChange' | 'onBlur' | 'onSubmit';
}

export interface ExtendedUseFormReturn<T extends FieldValues> extends UseFormReturn<T> {
  formProperties: Record<string, string>;
  formMethods: UseFormReturn<T>;
}

const useFormHook = <T extends FieldValues>({
  formSchema,
  formDefaultValues,
  schemaTitle,
  mode = 'onTouched',
  values,
  reValidateMode = 'onChange',
}: UseFormProps<T>): ExtendedUseFormReturn<T> => {
  const formMethods = useForm<T>({
    mode,
    resolver: zodResolver(formSchema),
    reValidateMode: reValidateMode,
    defaultValues: formDefaultValues as DefaultValues<T>,
    values,
  }) as UseFormReturn<T>;

  const jsonSchema = zodToJsonSchema(formSchema, schemaTitle).definitions?.[schemaTitle] as {
    properties: Record<string, string>;
  };

  const formProperties = jsonSchema?.properties;
  (formMethods as ExtendedUseFormReturn<T>).formProperties = formProperties;
  useEffect(() => {
    const handleFocusOut = (e: FocusEvent): void => {
      const elementId = (e.target as HTMLElement)?.id;
      if (elementId in formProperties) {
        // TODO - add analytics send to mixpanel per input on blur
      }
    };

    window.addEventListener('focusout', handleFocusOut);

    // Cleanup function to remove the event listener
    return () => {
      window.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  return { ...formMethods, formProperties, formMethods };
};

export default useFormHook;
